<script setup lang="ts">
import type { VSwitchProps } from "./VSwitch.types";
import { computed, useAttrs, useSlots, useTemplateRef } from "vue";
import { formErrorMessageCva } from "../Form/form.cva";
import { useFieldId } from "../useFieldId";
import { labelCva, requiredCva, rootCva, switchCva, thumbCheckedCva, thumbCva } from "./VSwitch.cva";

const {
  name = "",
  label = "",
  labelPosition = "left",
  required = false,
  trueValue = true,
  falseValue = false,
  errorMessage = "",
  hasPerms = true,
  disabledReason = undefined,
  size = "medium",
  rootClass = "",
  labelClass = "",
  ariaDescribedby = undefined,
  ariaLabelledby = undefined,
  ariaLabel = undefined,
  loading = false,
  id: rawId = undefined,
} = defineProps<VSwitchProps>();

defineSlots<{
  label: void
}>();

const modelValue = defineModel<any>({ default: false });

const attrs = useAttrs();
const isDisabled = computed(() => (typeof attrs.disabled !== "undefined" && attrs.disabled !== false) || !hasPerms || Boolean(disabledReason));
const slots = useSlots();
const id = useFieldId(() => rawId, { inheritFieldId: false });
const input = useTemplateRef<HTMLInputElement>("input");

/**
 * A click on the track toggles the switch. A click on the input itself, which a click on its label or Space also
 * dispatches, is left to the input's own v-model so the switch updates once.
 */
function onTrackClick(event: MouseEvent) {
  if (event.target === input.value || isDisabled.value || loading) {
    return;
  }
  modelValue.value = modelValue.value === trueValue ? falseValue : trueValue;
}

/** While the value is loading the input keeps its focus, but a click or Space does not change it. */
function onInputClick(event: MouseEvent) {
  if (loading) {
    event.preventDefault();
  }
}

const showLabel = computed(() => !!(label || slots.label));

const computedAriaInvalid = computed(() => {
  if (errorMessage) {
    return "true";
  }
  return undefined;
});

const errorId = computed(() => `${id.value}-error`);

const computedAriaDescribedby = computed(() => {
  const parts = [ariaDescribedby, errorMessage ? errorId.value : undefined].filter(Boolean);
  return parts.length ? parts.join(" ") : undefined;
});

const switchClasses = computed(() => {
  return {
    root: rootCva({
      labelPosition,
    }),
    switch: switchCva({
      size,
      disabled: isDisabled.value,
      errorMessage: !!errorMessage,
      active: modelValue.value === trueValue,
      hasPerms,
      loading: loading && !isDisabled.value,
    }),
    thumb: thumbCva({
      size,
    }),
    label: labelCva({
      size,
    }),
    thumbChecked: thumbCheckedCva({
      size,
    }),
    required: requiredCva({
      required,
    }),
    error: formErrorMessageCva(),
  };
});
</script>

<template>
  <div class="flex gap-x-2" :class="[switchClasses.root]">
    <label
      v-if="showLabel"
      :for="id"
      class="flex items-center gap-1 mb-1 text-gray-700 dark:text-white"
      :class="[switchClasses.label, labelClass]"
    >
      <slot name="label">{{ label }}</slot>
      <span :class="[switchClasses.required]">*</span>
    </label>

    <div>
      <div
        class="relative flex items-center justify-between w-full rounded-full"
        :class="[rootClass]"
        @click="onTrackClick"
      >
        <!-- Switch -->
        <ACTooltip
          type="edit_field"
          :allow-change="hasPerms"
          :disabled-reason="disabledReason"
        >
          <div class="relative">
            <input
              :id="id"
              ref="input"
              v-model="modelValue"
              type="checkbox"
              role="switch"
              :name="name"
              :disabled="isDisabled"
              :true-value="trueValue"
              :false-value="falseValue"
              class="peer sr-only"
              :aria-invalid="computedAriaInvalid"
              :aria-describedby="computedAriaDescribedby"
              :aria-labelledby="ariaLabelledby"
              :aria-label="ariaLabel"
              :aria-busy="loading || undefined"
              @click="onInputClick"
            >
            <div
              class="rounded-full transition-colors duration-200 peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500 peer-focus-visible:ring-offset-2 dark:peer-focus-visible:ring-offset-dark-800"
              :class="[switchClasses.switch]"
            >
              <div
                class="absolute top-1/2 -translate-y-1/2 rounded-full transition-all duration-200 bg-white dark:bg-white"
                :class="[
                  switchClasses.thumb,
                  modelValue === trueValue ? switchClasses.thumbChecked : '',
                ]"
              />
            </div>
          </div>
        </ACTooltip>
      </div>
      <p v-if="errorMessage" :id="errorId" :class="switchClasses.error">
        {{ errorMessage }}
      </p>
    </div>
  </div>
</template>
